import '../community-panel.css'
import '../../Admin/admin-shared.css'

import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ConfirmModal, PageHeader } from '../../../Components'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { columnProps } from '../../Prompts/Functions'

const language = cap.language.state || 'IT'
const t = translations[language]

export function CommunityPresetsSearch() {
  const { data: communityPresets = [], isSuccess } = useCQuery(['communityPresets'])
  const [communityPresetsTable, setCommunityPresetsTable] = useState([])
  const [defaultValues] = useState([t.yes, t.no])
  const [defaultStateValues] = useState([t.active, t.inactive])
  const navigate = useNavigate()

  useEffect(() => {
    isSuccess &&
      setCommunityPresetsTable(
        communityPresets.map((r) => ({
          ...r,
          createdAt: new Date(r.createdAt).toLocaleString(),
          defaultPreset: r.defaultPreset ? t.yes : t.no,
          state: r.state ? t.active : t.inactive
        }))
      )
  }, [communityPresets, isSuccess])

  const deleteOperation = async (presetId) => {
    const { defaultPreset = false } = communityPresets.find(({ id }) => id === presetId) || {}
    if (defaultPreset) {
      showToast('error', t.error, t.deleteDefaultPresetError)
      return false
    }
    try {
      const res = await FC.service('communityPreset').remove(presetId)
      invalidateQuery(['communityPresets'])
      showToast('success', t.success, t.deleteSuccess)
      return res
    } catch (error) {
      showToast('error', t.error || t.errore, error?.message || "Errore durante l'eliminazione")
      return false
    }
  }

  const BtnActionModify = ({ id }) => {
    const [modalVisible, setModalVisible] = useState(false)
    return (
      <div className="community-search-actions">
        <ConfirmModal
          header={t.deleteServiceHeader}
          label={t.deleteServiceLabel}
          onConfirm={() => deleteOperation(id)}
          visible={[modalVisible, setModalVisible]}
        />
        <button
          type="button"
          className="naction-btn"
          title={t.modifica}
          onClick={() => navigate('/community/panel/modify/' + id)}
        >
          <i className="pi pi-pencil" style={{ fontSize: 15 }} />
        </button>
        <button
          type="button"
          className="naction-btn"
          title={t.cpDuplica}
          onClick={() => navigate('/community/panel/duplicate/' + id)}
        >
          <i className="pi pi-copy" style={{ fontSize: 15 }} />
        </button>
        <button
          type="button"
          className="naction-btn"
          title={t.elimina}
          onClick={() => setModalVisible(true)}
        >
          <i className="pi pi-trash" style={{ fontSize: 15 }} />
        </button>
      </div>
    )
  }

  const statusRowFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={defaultValues}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={t.defaultPreset}
      />
    )
  }

  const statusRowStatuFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={defaultStateValues}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={t.abilitato}
      />
    )
  }

  const defaultPresetBody = ({ defaultPreset }) =>
    defaultPreset === t.yes ? (
      <span className="nbadge nbadge--info">
        <i className="pi pi-check" style={{ fontSize: 11, marginRight: 4 }} />
        {defaultPreset}
      </span>
    ) : (
      <span className="nbadge">{defaultPreset}</span>
    )

  const stateBody = ({ state }) => (
    <span className={`nora-status nora-status--${state === t.active ? 'ended' : 'neutral'}`}>
      <span className="nora-status__dot" />
      <span className="nora-status__label">{state}</span>
    </span>
  )

  return (
    <>
      <PageHeader
        title={t.communityPresetsList}
        subtitle={t.cpSottotitoloPanel}
        actions={
          <button
            type="button"
            className="nbtn nbtn--primary"
            onClick={() => navigate('/community/panel/new')}
          >
            <i className="pi pi-plus" style={{ fontSize: 14 }} />
            {t.createNew}
          </button>
        }
      />

      <div className="ncard admin-table-card" style={{ padding: 0 }}>
        <DataTable
          style={{ width: '100%' }}
          filterDisplay="row"
          sortField="createdAt"
          sortOrder={-1}
          className="TableSignatures"
          value={communityPresetsTable}
          emptyMessage={t.noPresetFound}
          responsiveLayout="scroll"
          paginator
          rows={10}
        >
          <Column
            field="id"
            {...columnProps(t.id)}
            body={({ id }) => <span className="ntable__num">#{id}</span>}
          />
          <Column
            field="titolo"
            {...columnProps(t.titolo)}
            body={({ titolo }) => <span style={{ fontWeight: 600, fontSize: 13.5 }}>{titolo}</span>}
          />
          <Column
            field="codiceCliente"
            {...columnProps(t.codiceCliente)}
            body={({ codiceCliente }) =>
              codiceCliente ? (
                <span className="nbadge" style={{ fontFamily: 'var(--font-mono)' }}>
                  {codiceCliente}
                </span>
              ) : (
                <span style={{ color: 'var(--ink-400)' }}>—</span>
              )
            }
          />
          <Column
            field="codiceGruppo"
            {...columnProps(t.codiceGruppo)}
            body={({ codiceGruppo }) =>
              codiceGruppo ? (
                <span className="ntable__num">{codiceGruppo}</span>
              ) : (
                <span style={{ color: 'var(--ink-400)' }}>—</span>
              )
            }
          />
          <Column
            field="descrizione"
            {...columnProps(t.descrizione)}
            body={({ descrizione = '', ragioneSociale = '' }) => (
              <span style={{ fontSize: 12.5, color: 'var(--ink-500)' }}>
                {descrizione || ragioneSociale}
              </span>
            )}
          />
          <Column
            field="defaultPreset"
            header={t.defaultPreset}
            headerStyle={{ visibility: 'hidden' }}
            filterMatchMode="contains"
            style={{ textAlign: 'center', border: 'none' }}
            showFilterMenu={false}
            showClearButton
            filter
            filterElement={statusRowFilterTemplate}
            body={defaultPresetBody}
          />
          <Column
            field="state"
            header={t.state}
            headerStyle={{ visibility: 'hidden' }}
            filterMatchMode="equals"
            style={{ textAlign: 'center', border: 'none' }}
            showFilterMenu={false}
            showClearButton
            filter
            filterElement={statusRowStatuFilterTemplate}
            body={stateBody}
          />
          <Column
            field="createdAt"
            {...columnProps(t.createdAt)}
            body={({ createdAt }) => (
              <span className="ntable__meta" style={{ fontFamily: 'var(--font-mono)' }}>
                {createdAt}
              </span>
            )}
          />
          <Column
            headerStyle={{ visibility: 'hidden' }}
            style={{ border: 'none', width: 150, textAlign: 'right' }}
            body={(props) => <BtnActionModify {...props} />}
          />
        </DataTable>
      </div>
    </>
  )
}
